import { FormEvent, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deleteReport, fetchReport, fetchReportParticipants, reportLogReport, updateReportName } from '../api/reports';
import { useAuth } from '../auth/AuthContext';
import { ErrorState } from '../components/ErrorState';
import { LoadingState } from '../components/LoadingState';
import type { EncounterSummary, ReportParticipantSummary } from '../api/types';
import { ReportEncounterTable } from '../components/reports/ReportEncounterTable';
import { formatDateTime, formatDecimal, formatDuration, formatNumber } from '../utils/format';
import {
  getActiveServerSlug,
  serverCharacterPath,
  serverDisplayName,
  serverGuildPath,
} from '../utils/siteContext';

type ReportMetric = 'DAMAGE' | 'HEALING';

function sum(values: number[]) {
  return values.reduce((total, value) => total + (Number.isFinite(value) ? value : 0), 0);
}

function reportDurationSeconds(startedAt: string | null, endedAt: string | null) {
  if (!startedAt || !endedAt) return 0;
  const started = new Date(startedAt).getTime();
  const ended = new Date(endedAt).getTime();
  if (!Number.isFinite(started) || !Number.isFinite(ended) || ended <= started) return 0;
  return Math.max((ended - started) / 1000, 1);
}

function canEditReport(userId: string | undefined, reportCreatedById: string | undefined) {
  return Boolean(userId && reportCreatedById && userId === reportCreatedById);
}

function classShortName(className: string | null | undefined) {
  const normalized = className?.trim();
  if (!normalized) return '???';

  const shorthand: Record<string, string> = {
    Bard: 'BRD',
    Beastlord: 'BST',
    Berserker: 'BER',
    Cleric: 'CLR',
    Druid: 'DRU',
    Enchanter: 'ENC',
    Magician: 'MAG',
    Monk: 'MNK',
    Necromancer: 'NEC',
    Paladin: 'PAL',
    Ranger: 'RNG',
    Rogue: 'ROG',
    'Shadow Knight': 'SHD',
    Shaman: 'SHM',
    Warrior: 'WAR',
    Wizard: 'WIZ',
  };

  return shorthand[normalized] ?? normalized.slice(0, 3).toUpperCase();
}

function ParticipantMeter({
  participants,
  metric,
  activeServerSlug,
}: {
  participants: ReportParticipantSummary[];
  metric: ReportMetric;
  activeServerSlug: string;
}) {
  const rows = useMemo(() => {
    const metricAmount = (participant: ReportParticipantSummary) => metric === 'DAMAGE' ? participant.damage : participant.healing;
    return [...participants]
      .filter((participant) => metricAmount(participant) > 0)
      .sort((a, b) => metricAmount(b) - metricAmount(a) || a.character.name.localeCompare(b.character.name));
  }, [participants, metric]);

  const total = sum(rows.map((participant) => metric === 'DAMAGE' ? participant.damage : participant.healing));
  const maxAmount = rows[0] ? (metric === 'DAMAGE' ? rows[0].damage : rows[0].healing) : 0;
  const amountLabel = metric === 'DAMAGE' ? 'Damage' : 'Healing';
  const rateLabel = metric === 'DAMAGE' ? 'DPS' : 'HPS';

  return (
    <section className="report-meter-section">
      <div className="card report-content-card">
        <div className="card-body">
          <div className="d-flex flex-wrap align-items-end justify-content-between gap-3 mb-3">
            <div>
              <h2 className="h5 mb-1">{metric === 'DAMAGE' ? 'Damage' : 'Healing'}</h2>
              <div className="small text-body-secondary">
                Aggregated report {metric === 'DAMAGE' ? 'damage' : 'healing'} across all linked encounters.
              </div>
            </div>
            <div className="small text-body-secondary tabular-nums">
              Total {amountLabel}: <span className="text-body">{formatNumber(total)}</span>
            </div>
          </div>

          {rows.length === 0 ? (
            <div className="text-body-secondary py-3">No {metric.toLowerCase()} records yet.</div>
          ) : (
            <div className="report-meter">
              {rows.map((participant, index) => {
                const amount = metric === 'DAMAGE' ? participant.damage : participant.healing;
                const rate = metric === 'DAMAGE' ? participant.dps : participant.hps;
                const percentOfTop = maxAmount > 0 ? Math.max(0, Math.min(100, (amount / maxAmount) * 100)) : 0;
                const percentOfTotal = total > 0 ? Math.max(0, Math.min(100, (amount / total) * 100)) : 0;

                return (
                  <div className="report-meter-row" key={`${participant.character.id}-${metric}`}>
                    <div className="report-meter-row__identity">
                      <div className="d-flex align-items-baseline gap-2">
                        <span className="rank-badge rank-badge--plain">#{index + 1}</span>
                        <Link className="link-light fw-semibold text-decoration-none" to={serverCharacterPath(activeServerSlug, participant.character)}>
                          {participant.character.name}
                        </Link>
                        <span className="class-shorthand">{classShortName(participant.character.className)}</span>
                      </div>
                      <div className="small text-body-secondary">
                        {formatDecimal(percentOfTotal, 1)}% of report {metric === 'DAMAGE' ? 'damage' : 'healing'}
                      </div>
                    </div>

                    <div className="report-meter-row__values">
                      <div className="tabular-nums fw-semibold">{formatNumber(amount)}</div>
                      <div className="small text-body-secondary">{formatDecimal(rate, 1)} {rateLabel}</div>
                    </div>

                    <div className="report-meter-row__bar" aria-label={`${participant.character.name} ${amountLabel} meter`}>
                      <div className="report-meter-row__fill" style={{ width: `${percentOfTop}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}


function encounterStartedTime(encounter: EncounterSummary) {
  const value = encounter.startedAt ? new Date(encounter.startedAt).getTime() : 0;
  return Number.isFinite(value) ? value : 0;
}

function sortEncountersChronological(encounters: EncounterSummary[]) {
  return [...encounters].sort((a, b) => encounterStartedTime(a) - encounterStartedTime(b));
}

function sortEncountersNewestFirst(encounters: EncounterSummary[]) {
  return [...encounters].sort((a, b) => encounterStartedTime(b) - encounterStartedTime(a));
}


export function ReportDetailPage() {
  const { reportId = '' } = useParams<{ reportId?: string }>();
  const activeServerSlug = getActiveServerSlug();
  const auth = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const reportQuery = useQuery({
    queryKey: ['report', reportId],
    queryFn: () => fetchReport(reportId),
    enabled: Boolean(reportId),
  });
  const participantsQuery = useQuery({
    queryKey: ['report', reportId, 'participants'],
    queryFn: () => fetchReportParticipants(reportId),
    enabled: Boolean(reportId),
  });
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [activeMetric, setActiveMetric] = useState<ReportMetric>('DAMAGE');
  const [reportReason, setReportReason] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [showReportForm, setShowReportForm] = useState(false);
  const [showDeleteForm, setShowDeleteForm] = useState(false);
  const updateNameMutation = useMutation({
    mutationFn: (name: string | null) => updateReportName(reportId, name),
    onSuccess: async () => {
      setEditingName(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['report', reportId] }),
        queryClient.invalidateQueries({ queryKey: ['reports'] }),
      ]);
    },
  });

  const report = reportQuery.data?.report;
  const encounters = reportQuery.data?.encounters ?? [];
  const participants = participantsQuery.data ?? [];
  const totalDamage = useMemo(() => sum(encounters.map((encounter) => encounter.totalDamage)), [encounters]);
  const totalHealing = useMemo(() => sum(encounters.map((encounter) => encounter.totalHealing ?? 0)), [encounters]);
  const bossCount = useMemo(() => new Set(encounters.filter((encounter) => encounter.raidBoss).map((encounter) => encounter.raidBoss?.id ?? encounter.raidBoss?.name)).size, [encounters]);
  const bossEncounters = useMemo(() => sortEncountersChronological(encounters.filter((encounter) => encounter.raidBoss)), [encounters]);
  const recentEncounters = useMemo(() => sortEncountersNewestFirst(encounters).slice(0, 20), [encounters]);
  const activeDuration = report ? reportDurationSeconds(report.startedAt, report.endedAt) : 0;
  const canEdit = canEditReport(auth.user?.id, report?.createdBy?.id) || auth.isAdmin;


  const reportContentMutation = useMutation({
    mutationFn: () => reportLogReport(reportId, reportReason.trim() || undefined),
    onSuccess: async () => {
      setReportReason('');
      setShowReportForm(false);
      await queryClient.invalidateQueries({ queryKey: ['report', reportId] });
    },
  });

  const deleteReportMutation = useMutation({
    mutationFn: () => deleteReport(reportId, deleteReason.trim() || undefined),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['reports'] });
      navigate('/reports');
    },
  });

  function submitReportContent(event: FormEvent) {
    event.preventDefault();
    reportContentMutation.mutate();
  }

  function submitDeleteReport(event: FormEvent) {
    event.preventDefault();
    deleteReportMutation.mutate();
  }

  function startEditingName() {
    if (!report) return;
    setNameDraft(report.name ?? report.displayName);
    setEditingName(true);
  }

  function submitName(event: FormEvent) {
    event.preventDefault();
    updateNameMutation.mutate(nameDraft.trim() || null);
  }

  return (
    <>
      <section className="page-hero">
        <div className="content-wrapper py-4">
          {reportQuery.isLoading ? <LoadingState label="Loading report" /> : null}
          {reportQuery.isError ? <ErrorState title="Could not load report" error={reportQuery.error} /> : null}

          {report ? (
            <>
              {!editingName ? (
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <h1 className="display-6 fw-semibold mb-0">{report.displayName}</h1>
                  {canEdit ? <button className="btn btn-sm btn-outline-light" type="button" onClick={startEditingName}>Rename</button> : null}
                  {auth.isAuthenticated ? <button className="btn btn-sm btn-outline-warning" type="button" onClick={() => setShowReportForm((value) => !value)}>Report</button> : null}
                  {canEdit ? <button className="btn btn-sm btn-outline-danger" type="button" onClick={() => setShowDeleteForm((value) => !value)}>Delete</button> : null}
                </div>
              ) : (
                <form className="d-flex flex-wrap gap-2 mt-2" onSubmit={submitName}>
                  <input className="form-control form-control-lg report-name-input" value={nameDraft} onChange={(event) => setNameDraft(event.target.value)} maxLength={120} />
                  <button className="btn btn-primary" type="submit" disabled={updateNameMutation.isPending}>Save</button>
                  <button className="btn btn-outline-secondary" type="button" onClick={() => setEditingName(false)}>Cancel</button>
                </form>
              )}
              {updateNameMutation.isError ? <div className="text-danger small mt-2">{updateNameMutation.error.message}</div> : null}
              {showReportForm ? (
                <form className="surface-card border rounded-3 p-3 mt-3" onSubmit={submitReportContent}>
                  <label className="form-label small text-body-secondary" htmlFor="report-reason">Report reason</label>
                  <textarea id="report-reason" className="form-control" rows={2} value={reportReason} onChange={(event) => setReportReason(event.target.value)} />
                  {reportContentMutation.isError ? <div className="text-danger small mt-2">{reportContentMutation.error.message}</div> : null}
                  {reportContentMutation.isSuccess ? <div className="text-success small mt-2">Report submitted for moderator review.</div> : null}
                  <div className="d-flex gap-2 mt-3">
                    <button className="btn btn-sm btn-warning" type="submit" disabled={reportContentMutation.isPending}>Submit report</button>
                    <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => setShowReportForm(false)}>Cancel</button>
                  </div>
                </form>
              ) : null}
              {showDeleteForm ? (
                <form className="surface-card border border-danger rounded-3 p-3 mt-3" onSubmit={submitDeleteReport}>
                  <label className="form-label small text-body-secondary" htmlFor="delete-reason">Delete reason</label>
                  <textarea id="delete-reason" className="form-control" rows={2} value={deleteReason} onChange={(event) => setDeleteReason(event.target.value)} />
                  <div className="small text-body-secondary mt-2">This hides the report from non-admin users and removes it from public report views.</div>
                  {deleteReportMutation.isError ? <div className="text-danger small mt-2">{deleteReportMutation.error.message}</div> : null}
                  <div className="d-flex gap-2 mt-3">
                    <button className="btn btn-sm btn-danger" type="submit" disabled={deleteReportMutation.isPending}>Delete report</button>
                    <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => setShowDeleteForm(false)}>Cancel</button>
                  </div>
                </form>
              ) : null}
              <span className="page-hero__server d-block">{report.server?.name ?? serverDisplayName(activeServerSlug)}</span>
              <p className="fs-3 mb-0">
                {report.guild ? (
                  <>
                    &lt;<Link className="link-light text-decoration-none" to={serverGuildPath(activeServerSlug, report.guild)}>{report.guild.name}</Link>&gt;
                  </>
                ) : (
                  <span>Personal Logs</span>
                )}
              </p>
            </>
          ) : null}
        </div>
      </section>

      {report ? (
        <>
          <section className="page-subnav">
            <div className="content-wrapper">
              <ul className="nav nav-tabs secondary-tabs">
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeMetric === 'DAMAGE' ? 'active' : ''}`}
                    type="button"
                    onClick={() => setActiveMetric('DAMAGE')}
                  >
                    Damage
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeMetric === 'HEALING' ? 'active' : ''}`}
                    type="button"
                    onClick={() => setActiveMetric('HEALING')}
                  >
                    Healing
                  </button>
                </li>
              </ul>
            </div>
          </section>

          <div className="content-wrapper page-main">
            <section className="mb-5">
              <h2 className="h5 mb-3">Overview</h2>
              <div className="report-overview-strip">
                <div className="report-overview-item"><small>Encounters</small><strong>{formatNumber(report.encounterCount)}</strong></div>
                <div className="report-overview-item"><small>Bosses</small><strong>{formatNumber(bossCount)}</strong></div>
                <div className="report-overview-item"><small>POVs</small><strong>{formatNumber(report.submissionCount)}</strong></div>
                <div className="report-overview-item"><small>Total Damage</small><strong>{formatNumber(totalDamage)}</strong></div>
                <div className="report-overview-item"><small>Raid DPS</small><strong>{formatDecimal(activeDuration > 0 ? totalDamage / activeDuration : 0, 1)}</strong></div>
                <div className="report-overview-item"><small>Total Healing</small><strong>{formatNumber(totalHealing)}</strong></div>
                <div className="report-overview-item"><small>Raid HPS</small><strong>{formatDecimal(activeDuration > 0 ? totalHealing / activeDuration : 0, 1)}</strong></div>
                <div className="report-overview-item"><small>Date</small><strong>{formatDateTime(report.startedAt ?? report.firstUploadAt)}</strong></div>
                <div className="report-overview-item"><small>Length</small><strong>{activeDuration > 0 ? formatDuration(activeDuration) : '—'}</strong></div>
                <div className="report-overview-item"><small>Uploader</small><strong>{report.createdBy?.username ?? '—'}</strong></div>
              </div>
            </section>

            <div className="d-flex flex-column gap-5">
              {participantsQuery.isLoading ? <LoadingState label="Loading report participants" /> : null}
              {participantsQuery.isError ? <ErrorState title="Could not load report participants" error={participantsQuery.error} /> : null}
              {participantsQuery.data ? (
                <ParticipantMeter participants={participants} metric={activeMetric} activeServerSlug={activeServerSlug} />
              ) : null}
              <ReportEncounterTable
                title="Boss Encounters"
                encounters={bossEncounters}
                activeServerSlug={activeServerSlug}
                emptyMessage="No boss encounters linked to this report yet."
              />
              <ReportEncounterTable
                title="Recent Encounters"
                encounters={recentEncounters}
                activeServerSlug={activeServerSlug}
                emptyMessage="No encounters linked to this report yet."
                footer={<Link className="btn btn-sm btn-outline-light" to={`/reports/${reportId}/encounters`}>View all encounters</Link>}
              />
            </div>
          </div>
        </>
      ) : null}
    </>
  );
}
